<template lang="pug">
  div(style="width: 100%")
    
    .p-4.has-text-centered(v-if="teamMembers.length === 0") 
      p(v-t="'appointment.no_service_available'")
    .list-item.centered-row.p-4.is-pushable(v-if="teamMembers.length > 1" @click="selectStaff()")
      .media.is-flex-grow-1
        .media-left
          .is-relative(style="width: 48px; height: 48px")
            figure.image.is-48x48.is-round(v-for="(picture, idx) in allMembersPictures" :key="idx" style="position:absolute; box-shadow: 0 0 4px rgba(0,0,0,0.1); width: 42px; height: 42px" :style="{left: `${idx * 4}px`}")
              img(:src="picture || require('@/assets/user.png')" @error="onInvalidImage")
        .media-body
          h5.title.is-6.is-marginless.has-text-primary(v-t="'appointment.all_staff_members'")
          p.subtitle.is-6.is-marginless
              small.has-text-grey(v-t="'appointment.staff_no_preference'")
          p(v-t="'appointment.staff_maximum_availability'")
      div
        span.icon
          i.mdi.mdi-arrow-right.mdi-24px
    
    .list-item.centered-row.p-4.is-pushable(v-for="member in teamMembers" :key="member._id" @click="selectStaff(member)" :style="{opacity: slotsByStaff[member._id].length ? 1 : 0.6}") 
        .media.is-flex-grow-1
          .media-left
            figure.image.is-48x48.is-round
              img(:src="member.picture || require('@/assets/user.png')" @error="onInvalidImage")
          .media-body
            h5.title.is-6.is-marginless {{member.firstName}} {{member.lastName}}
              span.has-text-primary(v-if="hasOverride")
                |  -  
                span(v-if="getPricePerMember(member._id).isFrom" v-t="'misc.from'")
                |   
                currency-view(:value="getPricePerMember(member._id).price")
                | , 
                duration-view(:value="getDurationPerMember(member._id)")
                | 
            p.subtitle.is-6.is-marginless
              small.has-text-grey-dark {{$t(`roles.${member.role}`)}} 
              small.has-text-grey-dark(v-if="member.level")
                |  •   
                span(v-t="`staff_levels.${member.level}`")  
            template(v-if="slotsByStaff[member._id].length")
              p.mb-1
                span(v-t="'appointment.earliest_availability'") 
                |  
                span  {{formatDate(slotsByStaff[member._id][0].start)}}
              p.tags
                span.tag.is-rounded(v-for="slot in slotsByStaff[member._id]") {{formatSlotLabel(slot)}}
            template(v-else)
              p.has-text-grey(v-t="'appointment.not_available'")
        div
          span.icon
            i.mdi.mdi-arrow-right.mdi-24px  
</template>

<script>
import dayjs from 'dayjs'
import {mapGetters, mapState} from 'vuex'
import slotsHelper from '../../../shared/slots'
import {getPricingOption} from '../../../shared/saleHelper'
export default {
  props: {
    locationId: {type: String, required: true},
    services: {type: Array, default: undefined},
    potentialStaff: {type: Array, default: undefined},
    potentialResources: {type: Array, default: undefined},

    occupiedSlots: {type: Object, default: undefined},
    rangesByDay: {type: Object, required: true},
    availableRanges: {type: Object, required: true},
    bookedResources: {type: Object, required: true}
  },
  data() {
    return {
      slotsByStaff: undefined
    }
  },
  computed: {
    ...mapState('staff', ['staffMap']),
    ...mapState('company', ['company']),
    ...mapGetters('staff', ['activeServiceMembersByLocations']),
    teamMembers() {
      if (this.potentialStaff) {
        return this.potentialStaff.map(s => this.staffMap[s])
      }
      return this.activeServiceMembersByLocations(this.currentLocationId)
    },
    hasOverride() {
      return this.services.reduce((res, s) => res || !!s.option.override, false)
    },
    allMembersPictures() {
      let pictures = this.teamMembers
        .filter(s => s.picture)
        .map(s => s.picture)
        .slice(0, 4)

      let res = Array(Math.max(Math.min(this.teamMembers.length, 4) - pictures.length, 0))
      res.fill(require('@/assets/user.png'), 0)
      return res.concat(pictures)
    }
  },
  created() {
    let slotsByStaff = {}
    for (let staff of this.teamMembers) {
      slotsByStaff[staff._id] = []

      let ranges = this.rangesByDay
      for (let day in ranges) {
        let resources = this.potentialResources.map(r => {
          return {resourceId: r, busySlots: this.bookedResources[r] && this.bookedResources[r][day]}
        })

        let daySlots = slotsHelper.generateSlots({
          day,
          preferred: true,
          ranges: this.availableRanges[staff._id][day],
          services: this.services,
          resources,
          staffId: staff._id,
          occupiedSlots: this.occupiedSlots,
          timeFormat: this.company.timeFormat,
          intervalType: this.company.onlineBookingInterval,
          bookAtLeastHours: this.company.bookAtLeastHours
        })

        if (daySlots.length) {
          slotsByStaff[staff._id] = daySlots.slice(0, 6)
          break
        }
      }
    }
    this.slotsByStaff = slotsByStaff
  },
  methods: {
    onInvalidImage(event) {
      event.target.src = require('@/assets/user.png')
    },
    selectStaff(staff) {
      if (staff && !this.slotsByStaff[staff._id].length) {
        return
      }
      this.loading = true
      setTimeout(() => {
        this.$emit('input', staff ? [staff._id] : this.teamMembers.map(s => s._id))
      }, 10)
    },
    formatSlotLabel(slot) {
      const format = this.company.timeFormat === '12' ? 'h:mma' : 'H:mm'
      const start = dayjs(slot.start).format(format)
      const end = dayjs(slot.end).format(format)
      return `${start} - ${end}`
    },
    formatDate(date) {
      return dayjs(date).format('LL')
    },
    getPricePerMember(staffId) {
      return this.services.reduce(
        (res, s) => {
          const {price, priceUpto} = getPricingOption(s.option, staffId)
          res.price += price
          res.isFrom = res.isFrom || (priceUpto !== undefined && priceUpto !== price)
          return res
        },
        {price: 0}
      )
    },
    getDurationPerMember(staffId) {
      return this.services.reduce((res, s) => res + getPricingOption(s.option, staffId).duration, 0)
    }
  }
}
</script>
<style scoped>
.list-item {
  background: white;
}
.list-item:hover {
  background: whitesmoke;
}
.centered-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
